import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import type { AggregateList } from 'src/components/followUp/common/types';

type Bars = { absoluteValue: number; label: string; relativeValue: number }[];

type UseExportOptionsProps = {
  bars: Bars;
  comparisonBars?: Bars;
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const mapRows: (bars: Bars) => Row[] = (bars) => [
  ...bars.map<Row>((bar) => [
    {
      value: bar.label,
      fontWeight: 'bold',
    },
    {
      value: bar.absoluteValue,
    },
    {
      value: `${bar.relativeValue}%`,
      align: 'right',
    },
  ]),
];

export const useExportOptions = ({
  bars,
  comparisonBars,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions => {
  const rows = useMemo(() => mapRows(bars), [bars]);
  const comparisonRows = useMemo(
    () => (comparisonBars ? mapRows(comparisonBars) : undefined),
    [comparisonBars],
  );
  return useMemo(
    () => ({
      widgetName: widgetName,
      rows,
      comparisonRows,
      queryFilters,
    }),
    [bars, widgetName, queryFilters],
  );
};

export const useDataMapper = () => {
  const { t } = useTranslation('followUp');

  return useCallback(
    (data?: AggregateList) =>
      data?.results.map((bar) => ({
        absoluteValue: bar.value,
        relativeValue: getRelativeValue(bar.value, data?.totalObjects ?? 0, 0),
        label: t(`statistics.responseFunnelWidget.${bar.label}`),
      })) ?? [],
    [t],
  );
};
